'use client';


/** Imports */
import Image from 'next/image';
import Select from 'react-select';
import {routing} from '@/i18n/routing';
import styles from './style.module.css';
import {useTRPCClient} from '@/trpc/client';
import {useLocale, useTranslations} from 'next-intl';
import {usePathname, useRouter} from 'next/navigation';
import {type Theme as SelectTheme} from 'react-select';
import {localeToLanguageCode} from '@/i18n/localeUtils';
import React, {useCallback, useMemo, useTransition} from 'react';


/** Component */
export default function LanguageSwitcher() {

    // Hooks
    const t = useTranslations('common.languages');
    const router = useRouter();
    const pathname = usePathname();
    const currentLocale = useLocale();
    const trpcClient = useTRPCClient();

    // State
    const [isPending, startTransition] = useTransition();
    const selectTheme: SelectTheme = {
        borderRadius: 16,
        colors: {
            primary: 'rgb(var(--color-primary-light) / .75)',
            primary75: 'rgb(var(--color-primary-light) / .75)',
            primary50: 'rgb(var(--color-primary-light) / .5)',
            primary25: 'rgb(var(--color-primary-light) / .25)',
            danger: 'red',
            dangerLight: 'red',
            neutral0: 'rgb(var(--color-primary-light))', //         <-- Input background color
            neutral5: 'rgb(var(--color-disabled-lighter))', //      <-- Disabled input background color
            neutral10: 'rgb(var(--color-primary-light))', //        <-- Inner border color
            neutral20: 'rgb(var(--color-text-on-primary) / .25)', //<-- Border color
            neutral30: 'rgb(var(--color-primary-light))', //        <-- Hover border
            neutral40: 'rgb(var(--color-primary-light) / .75)', //  <-- Hover
            neutral50: 'rgb(var(--color-gray-button-text))', //     <-- Placeholder color
            neutral60: 'rgb(var(--color-primary-light))',
            neutral70: 'rgb(var(--color-primary-light))',
            neutral80: 'rgb(var(--color-text-on-primary))', //      <-- Text color inside value
            neutral90: 'rgb(var(--color-primary-light))',
        },
        spacing: {
            baseUnit: 4,
            controlHeight: 40,
            menuGutter: 8,
        },
    };

    // Memos
    const options = useMemo(() => {
        return routing.locales.map((locale) => {
            const pathSplit = pathname.split('/');
            pathSplit[1] = locale;
            const newPath = pathSplit.join('/');
            const Inner = (<>
                <Image
                    src={`/images/flags/${locale}.svg`}
                    alt={locale}
                    width={24} height={20}
                    unoptimized={true}
                />
                {t(locale.toLocaleUpperCase())}
            </>);
            return {
                value: locale,
                label: (
                    <div className={styles.LanguageSwitcherOption}>
                        {Inner}
                    </div>
                ),
                labelString: locale,
                newPath,
            };
        });
    }, [t, pathname]);
    const defaultOption = useMemo(() => {
        return options.find((option) => (option.value === currentLocale));
    }, [currentLocale, options]);

    // Event handlers
    const handleLanguageChange = useCallback(async (newOption: typeof options[number] | null) => {
        if (!newOption || (newOption.value === currentLocale) || isPending) return;
        startTransition(async () => {
            try {
                const nextLanguageCode = localeToLanguageCode(newOption.value);
                if (nextLanguageCode) {
                    await trpcClient.userPreferences.updateLanguage.mutate({
                        preferredLanguage: nextLanguageCode,
                    });
                }
            } catch (error) {
                console.error('Failed to persist preferred language', error);
            }
            router.push(newOption.newPath);
            router.refresh();
        });
    }, [currentLocale, isPending, router, startTransition, trpcClient]);


    // Render
    if (!defaultOption || !selectTheme) return null;
    return (
        <div className={styles.LanguageSwitcher}>
            <Select
                instanceId="language-switcher"
                className={styles.LanguageSwitcherSelect}
                isSearchable={false}
                menuPlacement={'top'}
                theme={selectTheme}
                value={defaultOption}
                options={options as any}
                hideSelectedOptions={true}
                isDisabled={isPending}
                onChange={(newOption) => handleLanguageChange(newOption)}
            />
        </div>
    );
}
